/** @jsxImportSource solid-js */
import { createSignal, Show } from "solid-js";
import { createCodeInput } from "#palette/components/form/code.solid";
import { createEmailInput } from "#palette/components/form/email.solid";
import { SubmitButton } from "#palette/components/form/submit.solid";
import { Spinner } from "#palette/components/spinner.solid";
import { globalState } from "../../state";

/**
 * Display a page for survivors to verify their partner email address.
 */
export function Step_2_Email(props: {
  goNext: () => void;
  goUnknown: () => void;
  setEmail: (e: string) => void;
  setVerificationToken: (t: string) => void;
  setError: (m: string) => void;
  resetError: () => void;
}) {
  const emailAddress = createEmailInput({
    label: "Enter your university email",
    placeholder: "",
    tooltip: (
      <>
        <p class="py-2 font-bold">Your email is safe with us.</p>

        <p class="py-2">
          We use this email to verify that you have access to Callisto Vault. It
          is encrypted in our system. That means that no one will be able to see
          it, except you and a confidential advocate if (and only if) you match.
        </p>
        <p class="py-2 font-bold">
          You will have the opportunity to update this email to your personal
          email after you finish the account creation process.
        </p>
      </>
    ),
  });
  const code = createCodeInput({});
  let formElement!: HTMLFormElement;
  const [waitingForCode, setWaitingForCode] = createSignal(false);
  const [loading, setLoading] = createSignal(false);
  let verifyEncrypted: Uint8Array;

  function trySignup() {
    if (!emailAddress.valid()) {
      formElement.setAttribute("data-validate", "true");
      return;
    }

    props.resetError();
    setLoading(true);
    setWaitingForCode(false);
    setTimeout(async () => {
      props.setEmail(emailAddress.value());
      await globalState.client
        .verifyPartnerAffiliation_Step_1_Start(emailAddress.value())
        .then((ret) => {
          if (ret.isForeignEdu) {
            props.goUnknown();
            return;
          }

          verifyEncrypted = ret.encrypted;

          emailAddress.reset();
          setWaitingForCode(true);
        })
        .catch((e) => {
          console.error(e);
          props.setError(
            "We weren't able to send the verification email. Please try again.",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }, 500);
  }

  function ActualPage() {
    return (
      <>
        <div class="py-5">
          <h1 class="py-2 font-bold text-xl">
            We're so sorry for what brought you here.
          </h1>
          <p class="py-2">
            But we're so glad you found us. If you have experienced any kind of
            sexual harm, you deserve safety, support, and options - whether or
            not you report. If you have a .edu email address, you can access our
            encrypted tools, including the{" "}
            <a
              href="https://www.projectcallisto.org/callistovault/#matching"
              class="underline"
              target="_blank"
            >
              Matching System
            </a>{" "}
            and{" "}
            <a
              class="underline"
              href="https://www.projectcallisto.org/callistovault/#incidentlog"
              target="_blank"
            >
              Incident Log
            </a>
            , by creating your free account below. If you don't (yet) have
            access,{" "}
            <a
              href="https://www.projectcallisto.org/dayofreckoning"
              class="underline"
              target="_blank"
            >
              join us here
            </a>{" "}
            and help change that.
          </p>
          <p class="py-2">
            Please know you are not alone and what they did was NOT your fault.
          </p>
        </div>
        <form class="group" data-validate="false" ref={formElement}>
          {emailAddress.component}
          <SubmitButton onClick={trySignup} title={"Verify"} disabled={false} />
        </form>
        <div class="mt-5">
          <p>
            Already have an account?{" "}
            <a href="/log-in" style="underline">
              Login here
            </a>
            .
          </p>
        </div>
        {loading() && <Spinner />}
      </>
    );
  }

  function CodeEntry() {
    let codeForm!: HTMLFormElement;
    function tryVerifyCode() {
      if (code.value().length !== 6) {
        codeForm.setAttribute("data-validate", "true");
        return;
      }

      setLoading(true);
      props.resetError();

      setTimeout(async () => {
        await globalState.client
          .verifyPartnerAffiliation_Step_2_Decrypt(
            verifyEncrypted,
            code.value(),
          )
          .then((decryptedToken) => {
            props.setVerificationToken(decryptedToken);
            props.goNext();
          })
          .catch((e) => {
            console.error(e);
            props.setError(
              "We couldn't verify your account with that PIN. Please re-enter it and try again.",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }, 500);
    }

    return (
      <>
        <h1 class="my-3 text-xl font-bold text-brand-900 font-serif">
          Enter the verification code we sent
        </h1>

        <form class="group" data-validate="false" ref={codeForm}>
          {code.component}
          <SubmitButton
            onClick={tryVerifyCode}
            disabled={false}
            title={"Verify"}
          />
        </form>

        <div class="mt-8">
          <h1 class="font-bold my-2">Haven't received the code?</h1>
          <p>
            It can take up to 5 minutes for the email to show up on your end, or
            it might have found its way into your spam folder.{" "}
            <a
              onClick={() => {
                setWaitingForCode(false);
              }}
              style="underline"
            >
              Click here to go back and try again.
            </a>
          </p>
        </div>
        {loading() && <Spinner />}
      </>
    );
  }

  return (
    <Show when={waitingForCode()} fallback={<ActualPage />}>
      <CodeEntry />
    </Show>
  );
}
